import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from '../hooks/useToast';
import { useAuthStore } from '../store/authStore';
import { userService } from '../services/user.service';
import { getErrorMessage } from '../lib/api';
import { User as UserIcon, Camera, Lock } from 'lucide-react';
import { getProfilePictureUrl } from '../utils/avatar';

const profileSchema = z.object({
  email: z.string().email('Invalid email address'),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
});

const passwordSchema = z.object({
  current_password: z.string().min(1, 'Current password is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  password_confirmation: z.string(),
}).refine((data) => data.password === data.password_confirmation, {
  message: "Passwords don't match",
  path: ["password_confirmation"],
});

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

export const Profile = () => {
  const { user, updateUser } = useAuthStore();
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      email: user?.email || '',
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
    },
  });

  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  const onUpdateProfile = async (data: ProfileFormData) => {
    if (!user) return;

    setIsUpdatingProfile(true);
    try {
      const updatedUser = await userService.updateProfile(user.id, data);
      updateUser(updatedUser);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const onUpdatePassword = async (data: PasswordFormData) => {
    if (!user) return;

    setIsUpdatingPassword(true);
    try {
      await userService.updatePassword(user.id, data);
      toast.success('Password updated successfully');
      passwordForm.reset();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!user || !e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setIsUploadingImage(true);
    try {
      const updatedUser = await userService.uploadProfilePicture(user.id, file);
      updateUser(updatedUser);
      toast.success('Profile picture updated successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsUploadingImage(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <p className="text-sm text-gray-600 mt-1">
          Manage your account settings and preferences
        </p>
      </div>

      {/* Profile picture */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Picture</h3>
        <div className="flex items-center gap-6">
          <div className="relative">
            <img
              src={getProfilePictureUrl(user?.profile_picture_url)}
              alt={user?.full_name || 'User'}
              className="h-24 w-24 rounded-full object-cover"
            />
            {isUploadingImage && (
              <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                <div className="h-6 w-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
          <div>
            <label htmlFor="profile-picture" className="btn btn-primary cursor-pointer">
              <Camera className="h-4 w-4 inline mr-2" />
              Upload New Photo
            </label>
            <input
              id="profile-picture"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
              disabled={isUploadingImage}
            />
            <p className="text-xs text-gray-500 mt-2">
              JPG, PNG or GIF. Max size 5MB.
            </p>
          </div>
        </div>
      </div>

      {/* Profile information */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <UserIcon className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Profile Information</h3>
        </div>
        <form onSubmit={profileForm.handleSubmit(onUpdateProfile)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="first_name" className="label">First Name</label>
              <input
                {...profileForm.register('first_name')}
                id="first_name"
                type="text"
                className="input"
              />
              {profileForm.formState.errors.first_name && (
                <p className="mt-1 text-sm text-red-600">
                  {profileForm.formState.errors.first_name.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="last_name" className="label">Last Name</label>
              <input
                {...profileForm.register('last_name')}
                id="last_name"
                type="text"
                className="input"
              />
              {profileForm.formState.errors.last_name && (
                <p className="mt-1 text-sm text-red-600">
                  {profileForm.formState.errors.last_name.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="email" className="label">Email Address</label>
            <input
              {...profileForm.register('email')}
              id="email"
              type="email"
              className="input"
            />
            {profileForm.formState.errors.email && (
              <p className="mt-1 text-sm text-red-600">
                {profileForm.formState.errors.email.message}
              </p>
            )}
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isUpdatingProfile}
              className="btn btn-primary"
            >
              {isUpdatingProfile ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Change password */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
        </div>
        <form onSubmit={passwordForm.handleSubmit(onUpdatePassword)} className="space-y-4">
          <div>
            <label htmlFor="current_password" className="label">Current Password</label>
            <input
              {...passwordForm.register('current_password')}
              id="current_password"
              type="password"
              className="input"
            />
            {passwordForm.formState.errors.current_password && (
              <p className="mt-1 text-sm text-red-600">
                {passwordForm.formState.errors.current_password.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="label">New Password</label>
            <input
              {...passwordForm.register('password')}
              id="password"
              type="password"
              className="input"
            />
            {passwordForm.formState.errors.password && (
              <p className="mt-1 text-sm text-red-600">
                {passwordForm.formState.errors.password.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="password_confirmation" className="label">
              Confirm New Password
            </label>
            <input
              {...passwordForm.register('password_confirmation')}
              id="password_confirmation"
              type="password"
              className="input"
            />
            {passwordForm.formState.errors.password_confirmation && (
              <p className="mt-1 text-sm text-red-600">
                {passwordForm.formState.errors.password_confirmation.message}
              </p>
            )}
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isUpdatingPassword}
              className="btn btn-primary"
            >
              {isUpdatingPassword ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile;
